import { AxiosPromise } from "axios";
import { CONSTANT_CONFIG } from "@/constants";
import { HttpService } from "@/services/http.services";

const RESOURCE_ENDPOINT = `${CONSTANT_CONFIG.UTIL_SERVER_PREFIX}/messaging`;

const endpoints = {
    messages: () => `${RESOURCE_ENDPOINT}/messages`,
    message: (id: any) => `${RESOURCE_ENDPOINT}/messages/${id}`,
    lifecycle: (id: any, action: string) => `${RESOURCE_ENDPOINT}/messages/${id}/${action}`,
    recipients: (id: any) => `${RESOURCE_ENDPOINT}/messages/${id}/recipients`,
    resendRecipient: (id: any, recipientId: any) => `${RESOURCE_ENDPOINT}/messages/${id}/recipients/${recipientId}/resend`,
    history: () => `${RESOURCE_ENDPOINT}/messages/history`,
    recipientPreview: () => `${RESOURCE_ENDPOINT}/messages/recipient-preview`,
    my: () => `${RESOURCE_ENDPOINT}/messages/my`,
    myRead: (id: any) => `${RESOURCE_ENDPOINT}/messages/my/${id}/read`,
    myReadAll: () => `${RESOURCE_ENDPOINT}/messages/my/read-all`,
    publicAnnouncements: () => `${RESOURCE_ENDPOINT}/messages/public-announcements`,
    stagingRecipients: () => `${RESOURCE_ENDPOINT}/staging/recipients`,
    stagingSummary: (stagingId: any) => `${RESOURCE_ENDPOINT}/staging/${stagingId}/summary`,
    stagingList: (stagingId: any) => `${RESOURCE_ENDPOINT}/staging/${stagingId}/recipients`,
    stagingBulk: (stagingId: any) => `${RESOURCE_ENDPOINT}/staging/${stagingId}/recipients/bulk`,
};

export interface IStagingSummaryRow {
    source_key: string;
    source_type: string;
    title: string;
    recipients: number;
}

export interface IStagingSummary {
    message_staging_id: number;
    total: number;
    summary: IStagingSummaryRow[];
}

export default class MessagingApi {
    public list = (params = {}, headers = {}): AxiosPromise<any> => {
        return HttpService.get(endpoints.messages(), params, headers);
    };

    public getById = (id: any): AxiosPromise<any> => {
        return HttpService.get(endpoints.message(id));
    };

    public create = (payload = {}, params = {}, headers = {}): AxiosPromise<any> => {
        return HttpService.post(endpoints.messages(), payload, params, headers);
    };

    public update = (id: any, payload = {}, params = {}, headers = {}): AxiosPromise<any> => {
        return HttpService.put(endpoints.message(id), payload, params, headers);
    };

    public delete = (id: any): AxiosPromise<any> => {
        return HttpService.delete(endpoints.message(id));
    };

    public schedule = (id: any, scheduledAt: string): AxiosPromise<any> => {
        return HttpService.post(endpoints.lifecycle(id, "schedule"), { scheduled_at: scheduledAt });
    };

    public cancel = (id: any): AxiosPromise<any> => {
        return HttpService.post(endpoints.lifecycle(id, "cancel"));
    };

    public send = (id: any): AxiosPromise<any> => {
        return HttpService.post(endpoints.lifecycle(id, "send"));
    };

    // Sent-history detail drawer: per-recipient delivery breakdown.
    public recipients = (id: any, params = {}): AxiosPromise<any> => {
        return HttpService.get(endpoints.recipients(id), params);
    };

    // Re-queues one FAILED delivery row; backend clones it as a new recipient row
    // linked via parent_recipient_id rather than mutating the failed row in place.
    public resendRecipient = (id: any, recipientId: any): AxiosPromise<any> => {
        return HttpService.post(endpoints.resendRecipient(id, recipientId));
    };

    // Re-queues every FAILED recipient row on this message in one call
    // (messages/{id}/resend-failed) — bulk counterpart to resendRecipient.
    public resendFailed = (id: any): AxiosPromise<any> => {
        return HttpService.post(endpoints.lifecycle(id, "resend-failed"));
    };

    // Flat sent history — one row per recipient per channel, across all messages.
    public history = (params = {}): AxiosPromise<any> => {
        return HttpService.get(endpoints.history(), params);
    };

    public recipientsCount = (criteria: Record<string, number[]> = {}): AxiosPromise<any> => {
        return HttpService.post(endpoints.recipientPreview(), {
            source_type: 'role_area',
            source_data: {
                designation_ids: criteria.designationIds ?? [],
                area_ids: criteria.organizationIds ?? [],
                division_ids: criteria.divisionIds ?? [],
                district_ids: criteria.districtIds ?? [],
                upazila_ids: criteria.upazilaIds ?? [],
                union_ids: criteria.unionIds ?? [],
                block_ids: criteria.blockIds ?? [],
                organogram_ids: criteria.organogramIds ?? [],
            },
        });
    };

    public my = (params = {}): AxiosPromise<any> => {
        return HttpService.get(endpoints.my(), params);
    };

    // messages/my/{id}/read — id in the URL, no body (the old inbox/read shape posted
    // { id } to a route that no longer exists).
    public inboxRead = (id: any): AxiosPromise<any> => {
        return HttpService.post(endpoints.myRead(id));
    };

    // Inbox half of the bell's "Mark all as read" — the legacy notification endpoint
    // only clears message_queues and leaves these unread.
    public inboxReadAll = (): AxiosPromise<any> => {
        return HttpService.post(endpoints.myReadAll());
    };

    public publicAnnouncements = (): AxiosPromise<any> => {
        return HttpService.get(endpoints.publicAnnouncements());
    };

    // Resolves the criteria into individual recipient rows server-side and returns the
    // updated summary. Omit stagingId on the first call — the response mints one.
    public addStagingRecipients = (payload: Record<string, any>): AxiosPromise<any> => {
        return HttpService.post(endpoints.stagingRecipients(), payload);
    };

    public stagingSummary = (stagingId: any): AxiosPromise<any> => {
        return HttpService.get(endpoints.stagingSummary(stagingId));
    };

    // Paginated grid behind "View Recipients" — OData params, same as any list screen.
    public stagingRecipients = (stagingId: any, params = {}): AxiosPromise<any> => {
        return HttpService.get(endpoints.stagingList(stagingId), params);
    };

    // Removes staged recipients by id, or a whole source via { source_key }. Returns
    // the refreshed summary so the composer's totals need no second call.
    public stagingRecipientsBulk = (stagingId: any, payload: Record<string, any>): AxiosPromise<any> => {
        return HttpService.post(endpoints.stagingBulk(stagingId), payload);
    };
}